/**
 * EventsPage — Central Dispatch received events viewer.
 *
 * Shows events pushed by CD to our webhook, stored in cd_received_events.
 * Also allows searching historical events directly from the CD Event API.
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Radio, Search, Filter, X, RefreshCw, ChevronDown, ChevronUp,
  CheckCircle2, XCircle, Clock, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CD_EVENT_COLORS = {
  DispatchCreated:          'bg-blue-500/10 text-blue-400',
  DispatchUpdated:          'bg-slate-500/10 text-slate-400',
  DispatchAccepted:         'bg-cyan-500/10 text-cyan-400',
  DispatchVehiclePickedup:  'bg-indigo-500/10 text-indigo-400',
  DispatchVehicleDelivered: 'bg-emerald-500/10 text-emerald-400',
  DispatchCancelled:        'bg-red-500/10 text-red-400',
};

const FilterPill = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
      active
        ? 'bg-accent text-accent-foreground border-accent'
        : 'border-white/10 text-muted-foreground hover:border-white/30'
    }`}
  >
    {label}
  </button>
);

function EventRow({ event }) {
  const [expanded, setExpanded] = useState(false);
  const body = event.payload?.body || {};

  return (
    <div className="p-4 hover:bg-muted/20 transition-colors">
      <div className="flex items-start gap-4">
        <div className="mt-1 shrink-0">
          {event.processed
            ? <CheckCircle2 className="h-4 w-4 text-green-500" />
            : event.process_error
              ? <XCircle className="h-4 w-4 text-red-500" />
              : <Clock className="h-4 w-4 text-yellow-500" />
          }
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              variant="outline"
              className={`text-[11px] ${CD_EVENT_COLORS[event.event_type] || 'bg-muted text-muted-foreground'}`}
            >
              {event.event_type}
            </Badge>
            {event.load_id && (
              <span className="text-xs text-muted-foreground font-mono">
                Load: {event.load_id.slice(0, 8)}…
              </span>
            )}
            {event.cd_dispatch_id && (
              <span className="text-xs text-muted-foreground font-mono">
                CD Dispatch: {event.cd_dispatch_id.slice(0, 8)}…
              </span>
            )}
          </div>
          {event.process_error && (
            <p className="text-xs text-red-400 mt-1">{event.process_error}</p>
          )}
          {event.topic && (
            <p className="text-xs text-muted-foreground mt-0.5">{event.topic}</p>
          )}
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-1 flex items-center gap-1 text-xs text-accent/70 hover:text-accent transition-colors"
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {expanded ? 'Hide payload' : 'Show payload'}
          </button>
          {expanded && (
            <pre className="mt-2 p-2 rounded bg-muted/30 text-xs text-muted-foreground overflow-x-auto max-h-48">
              {JSON.stringify(event.payload, null, 2)}
            </pre>
          )}
        </div>
        <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
          {new Date(event.created_at).toLocaleString()}
        </span>
      </div>
    </div>
  );
}

function CDSearchRow({ item }) {
  const [expanded, setExpanded] = useState(false);
  const ev = item.eventData || {};
  return (
    <div className="p-4 hover:bg-muted/20 transition-colors border-b border-white/5 last:border-0">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={`text-[11px] ${CD_EVENT_COLORS[ev.eventType] || 'bg-muted text-muted-foreground'}`}>
              {ev.eventType || '—'}
            </Badge>
            <Badge
              variant="outline"
              className={`text-[10px] ${
                item.publishStatus === 'SUCCESS'
                  ? 'bg-green-500/10 text-green-400 border-green-500/20'
                  : 'bg-red-500/10 text-red-400 border-red-500/20'
              }`}
            >
              {item.publishStatus}
            </Badge>
            <span className="text-xs text-muted-foreground font-mono">
              {ev.eventId?.slice(0, 8)}…
            </span>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-1 flex items-center gap-1 text-xs text-accent/70 hover:text-accent"
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {expanded ? 'Hide' : 'Show'} event data
          </button>
          {expanded && (
            <pre className="mt-2 p-2 rounded bg-muted/30 text-xs text-muted-foreground overflow-x-auto max-h-48">
              {JSON.stringify(item, null, 2)}
            </pre>
          )}
        </div>
        <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
          {item.createdOn ? new Date(item.createdOn).toLocaleString() : '—'}
        </span>
      </div>
    </div>
  );
}

export default function EventsPage() {
  const { token } = useAuth();

  // Local received events
  const [received, setReceived]   = useState([]);
  const [loadingLocal, setLoadingLocal] = useState(true);
  const [search, setSearch]       = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [cdEventTypes, setCdEventTypes] = useState([]);

  // CD API search
  const [activeTab, setActiveTab] = useState('received'); // 'received' | 'search'
  const [cdSearch, setCdSearch]   = useState({ start_date: '', end_date: '', event_type: '', publish_status: '' });
  const [cdResults, setCdResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const fetchReceived = useCallback(async () => {
    setLoadingLocal(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (typeFilter) params.set('event_type', typeFilter);
      const res = await fetch(`${API_URL}/api/cd-events/received?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setReceived(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingLocal(false);
    }
  }, [token, typeFilter]);

  useEffect(() => { fetchReceived(); }, [fetchReceived]);

  useEffect(() => {
    const loadTypes = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cd-events/types`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setCdEventTypes((data.items || []).map(i => i.eventType));
        }
      } catch {}
    };
    loadTypes();
  }, [token]);

  const handleCdSearch = async () => {
    setSearching(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (cdSearch.start_date) params.set('start_date', cdSearch.start_date);
      if (cdSearch.end_date)   params.set('end_date', cdSearch.end_date);
      if (cdSearch.event_type) params.set('event_type', cdSearch.event_type);
      if (cdSearch.publish_status) params.set('publish_status', cdSearch.publish_status);
      const res = await fetch(`${API_URL}/api/cd-events/search?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'CD search failed');
      } else {
        setCdResults(await res.json());
      }
    } catch (e) {
      toast.error('CD search failed');
    } finally {
      setSearching(false);
    }
  };

  const filteredReceived = received.filter(ev => {
    if (typeFilter && ev.event_type !== typeFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      ev.event_type?.toLowerCase().includes(q) ||
      ev.load_id?.toLowerCase().includes(q) ||
      ev.cd_dispatch_id?.toLowerCase().includes(q) ||
      ev.cd_event_id?.toLowerCase().includes(q)
    );
  });

  const displayTypes = cdEventTypes.length > 0
    ? cdEventTypes
    : Object.keys(CD_EVENT_COLORS);

  const stats = {
    total:     received.length,
    processed: received.filter(e => e.processed).length,
    failed:    received.filter(e => e.process_error).length,
    matched:   received.filter(e => e.load_id).length,
  };

  return (
    <div className="space-y-4" data-testid="admin-cd-events">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl font-semibold flex items-center gap-2">
            <Radio className="h-6 w-6 text-accent" strokeWidth={1.5} />
            CD Event Stream
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Central Dispatch events received via webhook + historical search
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-full sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search events…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10 bg-transparent border-border"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={fetchReceived}
            className="shrink-0 border-white/10"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loadingLocal ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Received', value: stats.total, color: 'text-foreground' },
          { label: 'Load Matched', value: stats.matched, color: 'text-green-400' },
          { label: 'Processed', value: stats.processed, color: 'text-blue-400' },
          { label: 'Errors', value: stats.failed, color: 'text-red-400' },
        ].map(s => (
          <div key={s.label} className="rounded-xl border border-white/5 bg-card p-4">
            <p className="text-xs text-muted-foreground">{s.label}</p>
            <p className={`text-2xl font-semibold mt-1 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Tab selector */}
      <div className="flex gap-1 rounded-xl border border-white/5 bg-card p-1 w-fit">
        {['received', 'search'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors capitalize ${
              activeTab === tab
                ? 'bg-accent text-accent-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab === 'received' ? 'Received (Webhook)' : 'Search CD History'}
          </button>
        ))}
      </div>

      {activeTab === 'received' && (
        <>
          {/* Type filter */}
          <div className="rounded-xl border border-white/5 bg-card p-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground flex items-center gap-1 shrink-0">
                <Filter className="h-3 w-3" /> Type
              </span>
              <FilterPill label="All" active={!typeFilter} onClick={() => setTypeFilter('')} />
              {displayTypes.map(et => (
                <FilterPill
                  key={et}
                  label={et}
                  active={typeFilter === et}
                  onClick={() => setTypeFilter(typeFilter === et ? '' : et)}
                />
              ))}
              {(search || typeFilter) && (
                <button
                  onClick={() => { setSearch(''); setTypeFilter(''); }}
                  className="ml-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" /> Clear
                </button>
              )}
            </div>
          </div>

          <p className="text-xs text-muted-foreground px-1">
            {filteredReceived.length} of {received.length} event{received.length !== 1 ? 's' : ''}
          </p>

          <Card className="bg-card border-white/5">
            <CardContent className="p-0">
              {loadingLocal ? (
                <div className="text-center py-20 text-muted-foreground">Loading…</div>
              ) : filteredReceived.length === 0 ? (
                <div className="text-center py-20">
                  <Radio className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                  <p className="text-muted-foreground">
                    {typeFilter || search
                      ? 'No events match the current filters'
                      : 'No CD events received yet — create a subscription to start receiving events'}
                  </p>
                </div>
              ) : (
                <ScrollArea className="h-[550px]">
                  <div className="divide-y divide-white/5">
                    {filteredReceived.map(ev => <EventRow key={ev.id} event={ev} />)}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 'search' && (
        <Card className="bg-card border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Search className="h-4 w-4 text-accent" />
              Search Central Dispatch Event History
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">From date (UTC)</label>
                <Input
                  type="date"
                  value={cdSearch.start_date}
                  onChange={e => setCdSearch(f => ({ ...f, start_date: e.target.value }))}
                  className="bg-transparent border-border"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">To date (UTC)</label>
                <Input
                  type="date"
                  value={cdSearch.end_date}
                  onChange={e => setCdSearch(f => ({ ...f, end_date: e.target.value }))}
                  className="bg-transparent border-border"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Event Type</label>
                <select
                  value={cdSearch.event_type}
                  onChange={e => setCdSearch(f => ({ ...f, event_type: e.target.value }))}
                  className="w-full h-9 rounded-md border border-border bg-transparent px-3 text-sm text-foreground"
                >
                  <option value="">All types</option>
                  {displayTypes.map(et => <option key={et} value={et}>{et}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Publish Status</label>
                <select
                  value={cdSearch.publish_status}
                  onChange={e => setCdSearch(f => ({ ...f, publish_status: e.target.value }))}
                  className="w-full h-9 rounded-md border border-border bg-transparent px-3 text-sm text-foreground"
                >
                  <option value="">All statuses</option>
                  <option value="SUCCESS">SUCCESS</option>
                  <option value="FAILED">FAILED</option>
                </select>
              </div>
            </div>
            <Button
              onClick={handleCdSearch}
              disabled={searching}
              className="bg-accent hover:bg-accent/90 gap-1.5"
            >
              {searching ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              {searching ? 'Searching…' : 'Search CD Events'}
            </Button>

            {cdResults && (
              <div className="space-y-2 pt-2">
                <p className="text-xs text-muted-foreground">
                  {cdResults.count ?? cdResults.items?.length ?? 0} result(s)
                </p>
                <div className="rounded-xl border border-white/5 divide-y divide-white/5">
                  {(cdResults.items || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">No events found</p>
                  ) : (
                    (cdResults.items || []).map((item, i) => (
                      <CDSearchRow key={item.eventData?.eventId || i} item={item} />
                    ))
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
